var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { QueueType } from '../../multiskill/types';
import { useScenarioPlanIdByColorIndex } from '../../seatCapacity';
import { PLAN_COLORS } from '../../wizard/constants';
import { MainRow, QueueRow, getSeatsSummary } from '../common/periodSectionBody';
import { PeriodSectionTable } from '../common/periodSectionTable';
import { getCountryExpansionKey, getLobExpansionKey, getMultiskillExpansionKeys, getMultiskillGroupExpansionKeys, getMultiskillSectionExpansionKey, getQueueExpansionKeysForLob } from '../common/queueRow';
import { INDENT_LEVELS } from '../common/utils';
import { SEATS_REQUIRED_ROWS } from '../constants';
import { usePeriodReportChunkSize } from '../selectors';
var SEATS_REQUIRED_KEY = 'seatsRequired';
var getCountryExpansionItems = function (countries) {
  if (countries === void 0) {
    countries = [];
  }
  return countries.map(function (country) {
    return getCountryExpansionKey(SEATS_REQUIRED_KEY, country.countryName);
  });
};
var shouldUseCountryOnlyHierarchy = function (data) {
  var _a, _b;
  return !!((_a = data.countries) === null || _a === void 0 ? void 0 : _a.length) && !((_b = data.queuesByLob) === null || _b === void 0 ? void 0 : _b.length);
};
var SeatsRequiredSectionBody = function (_a) {
  var _b, _c;
  var data = _a.data,
    addColumns = _a.addColumns,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var t = useTranslation('report', {
    keyPrefix: 'period.reportRows'
  }).t;
  var rowColor = (_b = PLAN_COLORS[data === null || data === void 0 ? void 0 : data.colorIndex]) === null || _b === void 0 ? void 0 : _b[1];
  var isIncludeTotal = addColumns === null || addColumns === void 0 ? void 0 : addColumns.includes('Total');
  var isExpanded = (_c = expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[SEATS_REQUIRED_KEY]) !== null && _c !== void 0 ? _c : false;
  var isMultiskill = data.dataType === QueueType.Multiskill;
  var getScenarioPlanId = useScenarioPlanIdByColorIndex();
  var planId = getScenarioPlanId(data === null || data === void 0 ? void 0 : data.colorIndex);
  var countryOnly = shouldUseCountryOnlyHierarchy(data);
  var chunkSize = usePeriodReportChunkSize();
  return _jsxs(_Fragment, {
    children: [_jsx(MainRow, {
      bold: true,
      data: data.seatsRequired,
      calculated: getSeatsSummary(data, chunkSize, countryOnly),
      rowColor: rowColor,
      title: t('seatsRequired'),
      isHideRow: true,
      isIncludeTotal: isIncludeTotal,
      averagePrecision: 0,
      showDetails: isExpanded,
      onShowRow: function () {
        return onToggleItem === null || onToggleItem === void 0 ? void 0 : onToggleItem(SEATS_REQUIRED_KEY);
      },
      summaryValue: "max",
      isMultiskill: data.dataType === QueueType.Multiskill
    }), isExpanded && _jsx(QueueRow, {
      data: data.queues,
      dataWithLob: data.queuesByLob,
      countries: data.countries,
      isIncludeTotal: isIncludeTotal,
      keyItem: SEATS_REQUIRED_KEY,
      rowColor: rowColor,
      averagePrecision: 0,
      isMultiskill: isMultiskill,
      countryOnly: countryOnly,
      countryIndent: countryOnly ? INDENT_LEVELS.lob : undefined,
      locationIndent: countryOnly ? INDENT_LEVELS.queue : undefined,
      planId: planId,
      expandedItems: expandedItems,
      onToggleItem: onToggleItem
    })]
  });
};
export var SeatsRequiredTable = function (_a) {
  var data = _a.data,
    index = _a.index,
    addColumns = _a.addColumns,
    isProdHoursPlan = _a.isProdHoursPlan;
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  var countryOnly = shouldUseCountryOnlyHierarchy(data);
  var countryExpansionItems = React.useMemo(function () {
    return countryOnly ? getCountryExpansionItems(data.countries) : [];
  }, [countryOnly, data.countries]);
  var lobQueueExpansionItems = React.useMemo(function () {
    var _a;
    return ((_a = data.queuesByLob) !== null && _a !== void 0 ? _a : []).flatMap(function (lob) {
      return __spreadArray([getLobExpansionKey(lob.lobName, SEATS_REQUIRED_KEY)], getQueueExpansionKeysForLob(lob, SEATS_REQUIRED_KEY), true);
    });
  }, [data.queuesByLob]);
  var multiskillExpansionItems = React.useMemo(function () {
    return getMultiskillExpansionKeys(data.queues, SEATS_REQUIRED_KEY);
  }, [data.queues]);
  var multiskillGroupExpansionItems = React.useMemo(function () {
    return getMultiskillGroupExpansionKeys(data.queues, SEATS_REQUIRED_KEY);
  }, [data.queues]);
  var getNestedExpansionItems = function (itemName) {
    if (itemName === SEATS_REQUIRED_KEY) {
      return __spreadArray(__spreadArray(__spreadArray([], lobQueueExpansionItems, true), countryExpansionItems, true), multiskillExpansionItems, true);
    }
    if (itemName === getMultiskillSectionExpansionKey(SEATS_REQUIRED_KEY)) return multiskillGroupExpansionItems;
    return [];
  };
  return _jsx(PeriodSectionTable, {
    title: t('seatsRequired'),
    data: data,
    index: index,
    addColumns: addColumns,
    isProdHoursPlan: isProdHoursPlan,
    expansionItems: __spreadArray(__spreadArray(__spreadArray(__spreadArray([], SEATS_REQUIRED_ROWS, true), countryExpansionItems, true), lobQueueExpansionItems, true), multiskillExpansionItems, true),
    getNestedExpansionItems: getNestedExpansionItems,
    defaultExpanded: false,
    summaryColumnLabel: t('max', {
      defaultValue: 'Max'
    }),
    tableBodyComponent: SeatsRequiredSectionBody
  });
};